import React from "react";
import { Progress } from "antd";
import { SwapRightOutlined, SendOutlined } from "@ant-design/icons";
import { useDraggable, useDroppable } from "@dnd-kit/core";
import { ButtonItem } from "../../../../Components/Items";
import { iconoDeEtapa, colorDeEtapa } from "../EtapasOrdenProduccionPU/etapaVisual.utils";

const faltanteDe = (renglon) => Math.max(0, Math.round((renglon.cantidad - renglon.consumido) * 100) / 100);

export const TarjetaProductoEtapaPU = ({ color, producto, piezasPasadas = 0, cantidadEsperada = 0, children, style, refNodo, ...resto }) => (
  <div
    ref={refNodo}
    style={{
      backgroundColor: "white",
      borderRadius: 8,
      padding: "8px 10px",
      boxShadow: "0 1px 2px rgba(0,0,0,0.06)",
      ...style,
    }}
    {...resto}
  >
    <div style={{ fontSize: 12, fontWeight: 600 }}>{producto || "—"}</div>
    <Progress
      percent={cantidadEsperada > 0 ? Math.min(100, Math.round((piezasPasadas / cantidadEsperada) * 100)) : 0}
      size="small"
      showInfo={false}
      strokeColor={color}
      style={{ margin: "4px 0" }}
    />
    <div style={{ fontSize: 11, color: "#999" }}>
      Han pasado {piezasPasadas} / {cantidadEsperada} pzs
    </div>
    {children}
  </div>
);

const ColumnaEtapaOrdenPU = ({
  etapa,
  cantidad,
  renglonesConsumo,
  producto,
  piezasPasadas = 0,
  cantidadEsperada = 0,
  deshabilitado,
  esAbierta,
  onMoverPiezas,
  onSolicitarMaterial,
  anchoCompleto,
  refSoltar,
  estadoSoltar,
  arrastre,
  enviados = [],
}) => {
  const color = colorDeEtapa(etapa);
  const pendientes = renglonesConsumo.filter((r) => faltanteDe(r) > 0);
  const arrastrable = !!arrastre?.habilitado;

  const estiloColumna = anchoCompleto ? { width: "100%", display: "flex", flexDirection: "column" } : { flex: "0 0 280px", minWidth: 280, display: "flex", flexDirection: "column" };

  return (
    <div
      ref={refSoltar}
      style={{
        ...estiloColumna,
        transition: "opacity 0.15s",
        opacity: estadoSoltar === "invalida" ? 0.45 : 1,
      }}
    >
      <div
        style={{
          backgroundColor: "#ffffff",
          borderRadius: 6,
          padding: "12px 15px",
          border: "1px solid #f0f0f0",
          borderTop: `4px solid ${color}`,
          marginBottom: 5,
          boxShadow: "0 2px 5px rgba(0,0,0,0.02)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: "#5e6c84", textTransform: "uppercase" }}>
            <span style={{ color, marginRight: 6 }}>{iconoDeEtapa(etapa)}</span>
            {etapa.nombre}
          </span>
          <span style={{ backgroundColor: "#f4f5f7", padding: "2px 8px", borderRadius: 12, fontSize: 11, color: "#172b4d" }}>{cantidad}</span>
        </div>
      </div>

      <div
        style={{
          flex: 1,
          backgroundColor: estadoSoltar === "valida" || estadoSoltar === "encima" ? `${color}1f` : "#e6e6e636",
          border: estadoSoltar === "encima" ? `2px dashed ${color}` : estadoSoltar === "valida" ? `2px dashed ${color}80` : "2px dashed transparent",
          borderRadius: 6,
          padding: 6,
          minHeight: 120,
          transition: "background-color 0.15s, border-color 0.15s",
        }}
      >
        <TarjetaProductoEtapaPU
          color={color}
          producto={producto}
          piezasPasadas={piezasPasadas}
          cantidadEsperada={cantidadEsperada}
          refNodo={arrastrable ? arrastre.setNodeRef : undefined}
          style={arrastrable ? { cursor: "grab", opacity: arrastre.isDragging ? 0.4 : 1, touchAction: "none", userSelect: "none", WebkitUserSelect: "none" } : undefined}
          onDragStart={arrastrable ? (e) => e.preventDefault() : undefined}
          {...(arrastrable ? { ...arrastre.attributes, ...arrastre.listeners } : {})}
        >
          {renglonesConsumo.length > 0 && onSolicitarMaterial && (
            <ButtonItem
              size="small"
              gris
              block
              icon={<SendOutlined />}
              disabled={deshabilitado || (!esAbierta && pendientes.length === 0)}
              style={{ marginTop: 8 }}
              onClick={() => onSolicitarMaterial(esAbierta ? renglonesConsumo : pendientes)}
            >
              Solicitar material
            </ButtonItem>
          )}
        </TarjetaProductoEtapaPU>
        {enviados.map((envio, i) => (
          <div
            key={i}
            style={{ marginTop: 6, padding: "6px 10px", borderRadius: 8, background: "#ffffff", border: `1px dashed ${color}`, opacity: 0.55 }}
          >
            <div style={{ fontSize: 12, fontWeight: 600 }}>{envio.piezas} pzs enviadas a retrabajo</div>
            <div style={{ fontSize: 11, color: "#595959" }}>→ {envio.folio || "Orden de retrabajo"}</div>
          </div>
        ))}
      </div>

      <div style={{ backgroundColor: "white", padding: "8px 10px", borderRadius: "0 0 6px 6px" }}>
        {!etapa.esFinal && (etapa.transiciones || []).length > 0 && (
          <ButtonItem size="small" gris block icon={<SwapRightOutlined />} disabled={cantidad <= 0 || deshabilitado} onClick={() => onMoverPiezas(etapa)}>
            Mover piezas
          </ButtonItem>
        )}
      </div>
    </div>
  );
};

export const ColumnaEtapaOrdenPUArrastrable = (props) => {
  const { etapa, cantidad, deshabilitado, estadoSoltar } = props;
  const habilitado = !deshabilitado && !etapa.esFinal && (etapa.transiciones || []).length > 0 && cantidad > 0;
  const { setNodeRef: refSoltar } = useDroppable({ id: etapa._id, disabled: estadoSoltar === "invalida" || estadoSoltar === "origen" });
  const { setNodeRef, listeners, attributes, isDragging } = useDraggable({ id: `tarjeta-${etapa._id}`, data: { etapaId: etapa._id }, disabled: !habilitado });

  return <ColumnaEtapaOrdenPU {...props} refSoltar={refSoltar} arrastre={{ habilitado, setNodeRef, listeners, attributes, isDragging }} />;
};

export default ColumnaEtapaOrdenPU;
